import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

// ── Hardcoded fallback sections (shown if DB has no content yet) ──────────────
const FALLBACK_SECTIONS = [
  { id: "info-you-give", title: "1. Information You Give Us", content: [{ type: "p", text: "Loading..." }] },
];

// ── Anchor markers ─────────────────────────────────────────────────────────
// A line that is nothing but `<a id="some-id"></a>` is a marker, not content.
// It is pulled out of the line stream entirely before sections are built (so
// it never reaches RenderLines and prints itself as a raw tag), and gives
// the heading next to it a URL-stable id that does not have to match its
// heading text.
//
// Why a marker rather than always deriving the id from the heading: an
// outside party can require an EXACT fragment -- the consent checkbox's
// link to /privacy#voice-biometric-data is exactly this case -- that has
// nothing to do with whatever the heading happens to be worded today, and a
// heading's wording is precisely the thing most likely to get copyedited
// later. Slugifying the heading would silently break that link the next
// time someone rewords the section it points at. A marker is something the
// page's author sets on purpose and keeps even if the heading above it
// changes next month.
// Accepts the straight-quote form (<a id="x"></a>), a self-closing or
// unclosed variant (<a id="x" /> or <a id="x">), and the curly/smart
// quotes Google Docs silently substitutes for straight ones -- Somebi's
// source is a Google Doc, and that autocorrect is the single most likely
// way a hand-typed marker stops matching without anyone noticing.
const ANCHOR_LINE_RE = /^\s*<a\s+id=["'\u201C\u201D\u2018\u2019]([a-z0-9-]+)["'\u201C\u201D\u2018\u2019]\s*\/?\s*>(?:\s*<\/a>)?\s*$/i;

// ── Parse markdown into sections with headings for the ToC ───────────────────
function parseMarkdown(md) {
  if (!md) return [];
  const lines = md.split("\n");

  // First pass: find every anchor marker and decide which heading it names
  // -- the next "## " line after it if there is one (the normal case: the
  // marker sits on its own line right above the heading it labels),
  // otherwise the nearest one before it (the marker was left inside a
  // section that's already open). This runs before the real pass below so a
  // heading has its override, if any, ready the moment it turns into a
  // section.
  const headingLines = [];
  lines.forEach((line, i) => { if (line.startsWith("## ")) headingLines.push(i); });
  const idOverride = {};
  lines.forEach((line, i) => {
    const m = line.match(ANCHOR_LINE_RE);
    if (!m) return;
    const next = headingLines.find((h) => h > i);
    const prev = [...headingLines].reverse().find((h) => h < i);
    const target = next !== undefined ? next : prev;
    // Lowercased so a marker typed as "Voice-Biometric-Data" still lands on
    // the lawyer's exact, lowercase #voice-biometric-data fragment -- URL
    // fragments and getElementById are both case-sensitive, and casing is
    // an easy slip when hand-typing a slug.
    if (target !== undefined) idOverride[target] = m[1].toLowerCase();
  });

  const sections = [];
  let current = null;

  lines.forEach((line, i) => {
    if (ANCHOR_LINE_RE.test(line)) return; // marker only, never body content
    if (line.startsWith("## ")) {
      if (current) sections.push(current);
      const title = line.slice(3).trim();
      const id = idOverride[i] || title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      current = { id, title, lines: [] };
    } else if (line.startsWith("# ")) {
      // Top-level heading — skip (it's the doc title)
    } else {
      if (current) current.lines.push(line);
    }
  });
  if (current) sections.push(current);
  return sections;
}

// ── Render a block of markdown lines ─────────────────────────────────────────
function RenderLines({ lines }) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") { i++; continue; }

    // Bullet list
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <ul key={i} style={{ margin: "0 0 14px", padding: "0 0 0 22px" }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontSize: 15, lineHeight: 1.85, color: "var(--neutral-600)", marginBottom: 'var(--space-6)' }}>
              <RenderInline text={item} />
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Paragraph
    blocks.push(
      <p key={i} style={{ fontSize: 15, lineHeight: 1.85, color: "var(--neutral-600)", margin: "0 0 14px" }}>
        <RenderInline text={line} />
      </p>
    );
    i++;
  }
  return <>{blocks}</>;
}

// ── Render inline markdown (bold, italic) ────────────────────────────────────
function RenderInline({ text }) {
  // Handle **bold**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i} style={{ color: "#1B110D" }}>{part.slice(2, -2)}</strong>
          : part
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState(null);
  const [scrolled,      setScrolled]      = useState(false);
  const [tocOpen,       setTocOpen]       = useState(false);
  const [sections,      setSections]      = useState([]);
  const [updatedAt,     setUpdatedAt]     = useState("June 12, 2026");
  const [loading,       setLoading]       = useState(true);

  // Fetch from Supabase
  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from("legal_documents")
          .select("body, updated_at")
          .eq("key", "privacy")
          .maybeSingle();

        if (!error && data?.body) {
          const parsed = parseMarkdown(data.body);
          if (parsed.length > 0) {
            setSections(parsed);
            if (data.updated_at) {
              setUpdatedAt(new Date(data.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }));
            }
          }
        }
      } catch (err) {
        console.error("PrivacyPolicy fetch error:", err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, []);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [sections]);

  // Jump to a linked section once its content actually exists in the DOM.
  // Content loads from Supabase after mount, so the browser's own hash-jump
  // -- which only ever runs once, right after the very first paint -- fires
  // before any section exists and finds nothing to scroll to. This effect
  // runs AFTER `sections` is set instead, which is the first point a
  // matching id could possibly exist, so it does the jump itself rather than
  // relying on the browser to have gotten lucky on timing. A link like
  // https://reanloforreaders.com/privacy#voice-biometric-data (the consent
  // checkbox's required deep link, per the lawyer's wording) depends on this
  // to ever land anywhere at all.
  useEffect(() => {
    if (!sections.length) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [sections]);

  return (
    <div style={{ minHeight: "100vh", background: "#FCF9F8", fontFamily: 'var(--font-body)', color: "#1B110D" }}>
      <style>{`
        * { box-sizing: border-box; }
        .pp-topbar { position: sticky; top: 0; z-index: 50; padding: 14px 40px; display: flex; align-items: center; justify-content: space-between; transition: background 0.2s, box-shadow 0.2s; background: transparent; }
        .pp-topbar.scrolled { background: rgba(252,249,248,0.96); backdrop-filter: blur(8px); box-shadow: 0 1px 0 rgba(27,17,13,0.07); }
        .pp-hero { padding: 56px 40px 48px; border-bottom: 1px solid #e8e4dc; }
        .pp-layout { display: flex; max-width: 1060px; margin: 0 auto; padding: 44px 40px 96px; gap: 56px; align-items: flex-start; }
        .pp-sidebar { width: 188px; flex-shrink: 0; position: sticky; top: 76px; max-height: calc(100vh - 100px); overflow-y: auto; }
        .pp-main { flex: 1; min-width: 0; }
        .pp-toc-mobile { display: none; }
        @media (max-width: 768px) {
          .pp-topbar { padding: 12px 20px; }
          .pp-hero { padding: 36px 20px 32px; }
          .pp-layout { padding: 28px 20px 72px; flex-direction: column; gap: 0; }
          .pp-sidebar { display: none; }
          .pp-toc-mobile { display: block; margin-bottom: 32px; }
        }
      `}</style>

      {/* Top bar */}
      <div className={`pp-topbar${scrolled ? " scrolled" : ""}`}>
        <a href="/" style={{ color: "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-md)', fontWeight: 500 }}>← Reanlo</a>
        <a href="/terms" style={{ color: "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-base)' }}>Terms of Service →</a>
      </div>

      {/* Hero */}
      <header className="pp-hero">
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>
          <span style={{ display: "inline-block", background: "var(--feedback-warning-bg)", color: "#b45309", fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 6, marginBottom: 'var(--space-14)', border: "1px solid var(--feedback-warning-border)" }}>Legal</span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: "clamp(1.8rem, 5vw, 2.8rem)", fontWeight: 700, color: "#1B110D", margin: "0 0 6px", lineHeight: 1.1 }}>Privacy Policy</h1>
          <p style={{ fontSize: 'var(--font-size-base)', color: "var(--neutral-400)", margin: "0 0 14px", fontWeight: 500 }}>Last updated {updatedAt}</p>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: "var(--neutral-600)", maxWidth: 620, margin: "0 0 20px" }}>
            This Privacy Policy explains what information Reanlo for Readers and Reanlo for Authors ("Reanlo," "we," "us") collects, how we use it, and the choices you have. We've tried to write it in plain language.
          </p>
          <div style={{ background: "var(--brand-white)", border: "1px solid var(--feedback-warning-border)", borderRadius: 14, padding: "16px 20px", maxWidth: 620 }}>
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#b45309", margin: "0 0 10px" }}>The short version, up front</p>
            <ul style={{ margin: 'var(--space-none)', padding: "0 0 0 20px" }}>
              <li style={{ fontSize: 'var(--font-size-md)', lineHeight: 1.7, color: "var(--neutral-600)", marginBottom: 'var(--space-6)' }}>We don't use your content or data to train generative AI models, and we don't sell, share, or license it to anyone for that purpose.</li>
              <li style={{ fontSize: 'var(--font-size-md)', lineHeight: 1.7, color: "var(--neutral-600)" }}>We collect what we need to run the service and make book discovery work, nothing more.</li>
            </ul>
          </div>
        </div>
      </header>

      <div className="pp-layout">
        {/* Mobile TOC */}
        <div className="pp-toc-mobile">
          <button onClick={() => setTocOpen(!tocOpen)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderRadius: 'var(--radius-lg)', padding: "12px 16px", fontSize: 'var(--font-size-base)', fontWeight: 600, color: "#1B110D", cursor: "pointer" }}>
            <span>📋 Table of Contents</span>
            <span style={{ transform: tocOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
          </button>
          {tocOpen && (
            <nav style={{ background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "8px 0" }}>
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} onClick={() => setTocOpen(false)} style={{ display: "block", padding: "9px 16px", color: activeSection === s.id ? "var(--brand-rust)" : "var(--neutral-600)", fontWeight: activeSection === s.id ? 600 : 400, textDecoration: "none", fontSize: 'var(--font-size-base)' }}>{s.title}</a>
              ))}
            </nav>
          )}
        </div>

        {/* Desktop sidebar */}
        <aside className="pp-sidebar">
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--neutral-400)", margin: "0 0 12px" }}>Contents</p>
          <nav>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} style={{ display: "block", color: activeSection === s.id ? "#1B110D" : "var(--neutral-500)", textDecoration: "none", fontSize: 'var(--font-size-base)', lineHeight: 1.5, padding: "6px 0 6px 12px", marginLeft: -14, borderLeft: `2px solid ${activeSection === s.id ? "#d97706" : "transparent"}`, fontWeight: activeSection === s.id ? 600 : 400, transition: "color 0.15s, border-color 0.15s" }}>{s.title}</a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="pp-main">
          {loading ? (
            <div style={{ padding: "60px 0", textAlign: "center", color: "var(--neutral-400)", fontSize: 'var(--font-size-md)' }}>Loading...</div>
          ) : sections.length === 0 ? (
            <div style={{ padding: "60px 0", textAlign: "center", color: "var(--neutral-400)", fontSize: 'var(--font-size-md)' }}>No content yet. Add it in the admin panel under Config → Legal Pages.</div>
          ) : (
            sections.map((s) => (
              <section key={s.id} id={s.id} style={{ paddingBottom: 44, marginBottom: 44, borderBottom: "1px solid var(--brand-sand)", scrollMarginTop: 84 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: "1.2rem", fontWeight: 700, color: "#1B110D", margin: "0 0 16px", lineHeight: 1.3 }}>{s.title}</h2>
                <RenderLines lines={s.lines} />
              </section>
            ))
          )}

          {/* Contact card */}
          {!loading && sections.length > 0 && (
            <div style={{ background: "var(--brand-white)", border: "1px solid var(--brand-sand)", borderRadius: 'var(--radius-xl)', padding: "24px 28px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: "1rem", fontWeight: 700, color: "#1B110D", margin: "0 0 6px" }}>Questions about this policy?</p>
              <p style={{ fontSize: 'var(--font-size-md)', color: "var(--neutral-600)", margin: "0 0 10px", lineHeight: 1.6 }}>
                Contact us at <a href="mailto:ehss@reanloforreaders.com" style={{ color: "var(--feedback-warning)", fontWeight: 600, textDecoration: "none" }}>ehss@reanloforreaders.com</a>
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
